import { memo } from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { Link as LinkIcon, StickyNote, Info } from "lucide-react";
import { NodeData } from "../lib/api";

function contrastColor(hexColor: string | null | undefined): string {
  if (!hexColor) return "#000000";
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#ffffff";
}

function lighten(hexColor: string | null | undefined, amount: number = 20): string {
  if (!hexColor) return "#e5e7eb";
  const hex = hexColor.replace("#", "");
  const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + amount);
  const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + amount);
  const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + amount);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function toRgba(hexColor: string | null | undefined, alpha: number = 0.1): string {
  if (!hexColor) return `rgba(229, 231, 235, ${alpha})`;
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const EllipseNode = memo(({ data, selected }: NodeProps<NodeData>) => {
  const bgColor = data.color || "#3b82f6";
  const textColor = data.text_color || contrastColor(bgColor);
  const borderColor = lighten(bgColor, 30);
  const bgRgba = toRgba(bgColor, 0.15);

  const hasInfo = !!(data.description || (data.links && data.links.length > 0) || (data.notes && data.notes.length > 0));
  const linksCount = data.linksCount ?? data.links?.length ?? 0;
  const notesCount = data.notesCount ?? data.notes?.length ?? 0;

  const isHighlighted = data.highlighted ?? false;

  return (
    <div className="relative group">
      {/* Ellipse Shape Container */}
      <div className="relative" style={{ width: "200px", height: "120px" }}>
        {/* SVG Ellipse */}
        <svg
          width="200"
          height="120"
          viewBox="0 0 200 120"
          className={`
            transition-all duration-200
            ${isHighlighted ? "filter brightness-110" : ""}
            ${selected ? "drop-shadow-lg" : "drop-shadow-md"}
            ${data.inactive ? "opacity-65" : ""}
          `}
        >
          {/* Ellipse */}
          <ellipse
            cx="100"
            cy="60"
            rx="90"
            ry="50"
            fill={bgRgba}
            stroke={borderColor}
            strokeWidth={data.inactive ? "3" : "2.5"}
            strokeDasharray={data.inactive ? "8,4" : "0"}
            className="transition-all"
          />

          {/* Selected outline */}
          {selected && (
            <ellipse
              cx="100"
              cy="60"
              rx="90"
              ry="50"
              fill="none"
              stroke="#3b82f6"
              strokeWidth="4"
              className="animate-pulse"
              opacity="0.5"
            />
          )}
        </svg>

        {/* Content overlay */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ padding: "20px 30px" }}
        >
          <div className="text-center w-full">
            {/* Label */}
            <div
              className="font-semibold text-sm leading-tight line-clamp-2 px-2"
              style={{
                color: textColor,
                textShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }}
            >
              {data.label}
            </div>

            {/* Info indicators */}
            {(hasInfo || linksCount > 0 || notesCount > 0) && (
              <div className="flex items-center justify-center gap-1 mt-2">
                {hasInfo && (
                  <div
                    className="flex items-center justify-center w-5 h-5 rounded-full border"
                    style={{
                      backgroundColor: toRgba(bgColor, 0.3),
                      borderColor: borderColor,
                      color: textColor,
                    }}
                  >
                    <Info className="w-3 h-3" />
                  </div>
                )}
                {linksCount > 0 && (
                  <div
                    className="flex items-center justify-center gap-0.5 px-1.5 py-0.5 rounded-full border text-xs font-medium"
                    style={{
                      backgroundColor: toRgba(bgColor, 0.3),
                      borderColor: borderColor,
                      color: textColor,
                    }}
                  >
                    <LinkIcon className="w-3 h-3" />
                    {linksCount}
                  </div>
                )}
                {notesCount > 0 && (
                  <div
                    className="flex items-center justify-center gap-0.5 px-1.5 py-0.5 rounded-full border text-xs font-medium"
                    style={{
                      backgroundColor: toRgba(bgColor, 0.3),
                      borderColor: borderColor,
                      color: textColor,
                    }}
                  >
                    <StickyNote className="w-3 h-3" />
                    {notesCount}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Handles */}
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          className="atlas-handle !bg-blue-500 !w-3 !h-3 !border-2 !border-white"
          style={{ top: "10px", left: "50%" }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          className="atlas-handle !bg-emerald-500 !w-3 !h-3 !border-2 !border-white"
          style={{ top: "50%", right: "10px" }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className="atlas-handle !bg-emerald-500 !w-3 !h-3 !border-2 !border-white"
          style={{ bottom: "10px", left: "50%" }}
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          className="atlas-handle !bg-amber-500 !w-3 !h-3 !border-2 !border-white"
          style={{ top: "50%", left: "10px" }}
        />
      </div>
    </div>
  );
});

EllipseNode.displayName = "EllipseNode";
